import { useParams } from "react-router-dom";
import { useState } from "react";
import Button from "@/common/components/Button";
import { ImageUploader } from "@/common/components/ImageUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import SelectController from "@/common/components/SelectController";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { Path, SubmitHandler, useForm } from "react-hook-form";
import { userSchema, UserValidationSchema } from "@/common/schemas/create.schema";
import Box from "@/common/components/Box";
import { dateTransformer } from "@/common/utils/dateTransformer";
import { FilterOptionOption } from "node_modules/react-select/dist/declarations/src/filters";
import useDebounce from "@/common/hooks/useDebounce";
import { statusOptions } from "../../constants/statusOptions.constant";
import { filterOptions } from "../../constants/filterOptions.constant";
import { phoneMask } from "@/common/constants/masks.constant";
import useUser from "@/common/hooks/useUser"
import { useGeosOptions } from "@/common/hooks/queries/useGeosOptions";
import useUpdateUser from "@/common/hooks/useUpdateUser";
import { checkIsImage } from "@/common/utils/fileValidator";
import { transformRolesToOptions } from "@/common/utils/roleTransformers";
import { useRoles } from "@/common/hooks/queries/useRoles";
import { Mapper } from "@/common/services/mapper";
import { RoleData } from "@/common/types/roles";
import { FormTransformers } from "@/common/utils/formTransformers";
import { GeoTransformers } from "@/common/services/geoTransformers";
import useCpfDetails from "@/common/hooks/queries/useCpfDetails";
import { ImportDetailsMapper } from "@/common/services/importDetailsMapper";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import GoBackButton from "@/common/components/GoBackButton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { authenticationMethodOptions } from "@/features/users/constants/authenticationMethodOptions.constant";
import OAuthProviderField from '@/common/components/OAuthProviderField';

const Update = () => {
  const goBack = useGoBack()
  const { id } = useParams();
  const [file, setFile] = useState<Blob>();
  const [geoSearch, setGeoSearch] = useDebounce('');
  const [searchRoles, setSearchRoles] = useDebounce('');
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { data: user, isLoading } = useUser({ id: id || '' });

  const onSuccess = () => {
    goBack({ fallback: `/users/${id}` });
  }

  const { data: rolesOptions } = useRoles({
    filters: {
      search: searchRoles,
    },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  })

  const geos = useGeosOptions(geoSearch)

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    clearErrors,
    formState: { errors }
  } = useForm<UserValidationSchema>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      ...user,
      date_of_birth: user?.date_of_birth ? dateTransformer(user?.date_of_birth) : '',
      roles: transformRolesToOptions(user?.user_roles || []),
      status: statusOptions.find((status) => user?.status === status.value),
      geos: GeoTransformers.mapToGeo()(user?.geo_geoables),
      sicof_user_identification: user?.sicof_user_identification || '',
      authentication_method: user?.authentication_method
        ? authenticationMethodOptions.find((option) => option.value === user.authentication_method)
        : undefined,
    }
  })

  const {
    mutate: updateUser,
    isPending: updateUserIsPending,
  } = useUpdateUser({
    user,
    file,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UserValidationSchema>({
      setError,
      setMessages,
    }),
  })

  const onSubmit: SubmitHandler<UserValidationSchema> = (data) => updateUser(data);

  const { mutate: importData, isPending: importDataIsPending } = useCpfDetails({
    onSuccess: (response) => {
      clearErrors('cpf');

      ImportDetailsMapper.cpfDetails()(response.data.user).forEach(([field, value]) => {
        clearErrors(field as Path<UserValidationSchema>);
        setValue(field as Path<UserValidationSchema>, value);
      });
    },
    onError: (error) => {
      setError('cpf', { message: error?.response?.data?.errors?.base?.[0] });
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <GoBackButton fallback={`/users/${id}`} searchLastRouteFromFallback />
          <h2 data-testid="display_name_update" className="text-2xl font-bold">{user?.full_name}</h2>
        </div>
        <Button
          name="submit"
          type="submit"
          className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
          disabled={updateUserIsPending}
          isLoading={updateUserIsPending}
        >
          Confirmar
        </Button>
      </div>
      {isLoading || !geos
        ? (<Box>Carregando...</Box>)
        : <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] h-[calc((100vh-40px-4rem-1.5rem))] overflow-y-auto">
          <p className="text-lg font-bold">Dados pessoais</p>
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsImage(file)) {
                return
              }
              setFile((e.target as HTMLInputElement & { files: FileList }).files[0])
            }}
            currentImageUrl={file
              ? URL.createObjectURL(file)
              : user?.profile_picture?.url
                ? user?.profile_picture?.url
                : undefined
            }
            disabled={updateUserIsPending}
          />
          <div className="flex gap-2 w-full justify-between">
            <div className="w-3/4">
              <MaskedTextField
                label="CPF"
                mask='000.000.000-00'
                errorMessage={errors.cpf?.message || undefined}
                defaultValue={user?.cpf}
                {...register('cpf')}
                disabled={updateUserIsPending}
              />
              {importDataIsPending ? (
                <p className="text-sm text-blue-400">Importando dados</p>
              ) : (
                <Button variant='link' onClick={() => importData(watch('cpf'))} disabled={updateUserIsPending}>Importar dados</Button>
              )}
            </div>
            <MaskedTextField
              label="Data de nascimento"
              mask='00/00/0000'
              className="w-1/4"
              errorMessage={errors.date_of_birth?.message || undefined}
              defaultValue={user?.date_of_birth ? dateTransformer(user?.date_of_birth) : ''}
              {...register('date_of_birth')}
              disabled={updateUserIsPending}
              markAsRequired
            />
          </div>
          <div className="flex gap-2 w-full justify-between">
            <TextField
              label="Nome"
              maxLength={64}
              className="w-3/4"
              errorMessage={errors.full_name?.message || undefined}
              defaultValue={user?.full_name}
              {...register('full_name')}
              disabled={updateUserIsPending}
              markAsRequired
            />
            <SelectController
              control={control}
              name="roles"
              label="Perfil"
              className="w-1/4"
              options={rolesOptions || []}
              onInputChange={setSearchRoles}
              isMulti
              markAsRequired
              isDisabled={updateUserIsPending}
              errorMessage={errors.roles?.message || undefined}
            />
          </div>
          <SelectController
            control={control}
            name="status"
            label="Status"
            defaultValue={statusOptions.find((status) => user?.status === status.value)}
            options={statusOptions}
            isDisabled={updateUserIsPending}
          />
          <SelectController
            control={control}
            name="geos"
            label="Região de atuação"
            isMulti
            onInputChange={setGeoSearch}
            filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
            defaultValue={GeoTransformers.mapToGeo()(user?.geo_geoables)}
            options={GeoTransformers.mapToGeoOptions()(geos)}
            isDisabled={updateUserIsPending}
          />
          <SelectController
            control={control}
            name="authentication_method"
            label="Configuração de login"
            options={authenticationMethodOptions}
            errorMessage={errors.authentication_method?.message || undefined}
            isDisabled={updateUserIsPending}
            markAsRequired
          />
          <MaskedTextField
            label="Telefone"
            mask={phoneMask}
            errorMessage={errors.phone?.number?.message || undefined}
            defaultValue={user?.phone?.number || undefined}
            {...register('phone.number')}
            disabled={updateUserIsPending}
          />
          <div className='flex gap-2 w-full items-end'>
            <TextField
              label="Email"
              errorMessage={errors.email?.message || undefined}
              defaultValue={user?.email}
              {...register('email')}
              disabled={updateUserIsPending || watch('authentication_method.value') === 'oauth'}
              markAsRequired
              className='w-full'
            />

            {watch('authentication_method.value') === 'oauth' && <OAuthProviderField user={user} />}
          </div>
          <MaskedTextField
            label="Código SICOF"
            mask='000'
            {...register('sicof_user_identification')}
            errorMessage={errors.sicof_user_identification?.message || undefined}
            disabled={updateUserIsPending}
          />
        </Box>
      }

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  )
}

export default Update
